import { useCallback, useRef, useState } from 'react'
import { useAppSelector } from 'hooks/reduxHooks'
import { searchFlights } from 'api/flights'
import type { MappedFlightSearchResult } from 'api/flights'
import type { FlightSearchParams } from 'hooks/useSearchFlights'
import { FLIGHTS_SESSION_EXPIRATION_TIME_IN_MINUTES } from 'constants/flight'
import { isLeFlightSavedItemData, type TransportOption } from 'tripPlanner/api/types'
import { parseSearchParams } from './flightShortlistUtils'
import { reportLog } from 'services/userMonitoring'
import { findShortlistedFlightJourneys } from './shortlistedFlightJourneyMatching'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'

type RefreshStatus = 'idle' | 'refreshing' | 'success' | 'error'

export interface RefreshedFlight {
  option: TransportOption
  matchedJourney?: App.JourneyV2
  matchedDepartingJourney?: App.JourneyV2
  matchedReturningJourney?: App.JourneyV2
  newPrice?: number
  newRegularPrice?: number
  isLuxPlusFare?: boolean
  newCurrencyCode?: string
  newExpiry?: number
  isSoldOut: boolean
}

interface UseRefreshResult {
  status: RefreshStatus
  refreshedFlights: Array<RefreshedFlight>
  refresh: (options: Array<TransportOption>) => Promise<Array<RefreshedFlight>>
}

const MAX_REFRESH_CACHE_SIZE = 50
const cachedRefreshResults = new Map<string, RefreshedFlight>()

function getRefreshCacheKey(option: TransportOption, currency: string, region: string | undefined, email: string | undefined): string | undefined {
  const data = option.savedItemData
  if (!isLeFlightSavedItemData(data) || option.itemId === undefined || !data.searchParams || !data.journeyKey) return undefined

  const returnLegData = isLeFlightSavedItemData(option.returnLeg?.savedItemData)
    ? option.returnLeg.savedItemData
    : undefined

  return [
    option.itemId,
    currency,
    region ?? '',
    email ?? '',
    data.journeyKey,
    returnLegData?.journeyKey ?? '',
    data.searchExpiry ?? '',
    data.searchParams,
  ].join('|')
}

function getCachedRefreshResult(option: TransportOption, cacheKey: string | undefined, now: number): RefreshedFlight | undefined {
  if (!cacheKey) return undefined

  const cached = cachedRefreshResults.get(cacheKey)
  if (!cached) return undefined

  if (!cached.newExpiry || cached.newExpiry <= now) {
    cachedRefreshResults.delete(cacheKey)
    return undefined
  }

  return {
    ...cached,
    option,
  }
}

function cacheRefreshResult(result: RefreshedFlight, cacheKey: string | undefined, now: number) {
  if (!cacheKey || !result.newExpiry || result.newExpiry <= now) return

  evictExpiredRefreshResults(now)
  if (cachedRefreshResults.size >= MAX_REFRESH_CACHE_SIZE && !cachedRefreshResults.has(cacheKey)) {
    const oldestKey = cachedRefreshResults.keys().next().value
    if (oldestKey) cachedRefreshResults.delete(oldestKey)
  }

  if (cachedRefreshResults.has(cacheKey)) {
    cachedRefreshResults.delete(cacheKey)
  }
  cachedRefreshResults.set(cacheKey, result)
}

function evictExpiredRefreshResults(now: number) {
  for (const [key, entry] of cachedRefreshResults) {
    if (!entry.newExpiry || entry.newExpiry <= now) {
      cachedRefreshResults.delete(key)
    }
  }
}

function useRefreshShortlistedFlights(): UseRefreshResult {
  const [status, setStatus] = useState<RefreshStatus>('idle')
  const [refreshedFlights, setRefreshedFlights] = useState<Array<RefreshedFlight>>([])
  const hasRefreshedRef = useRef(false)

  const currency = useAppSelector(state => state.geo.currentCurrency)
  const region = useAppSelector(state => state.geo.currentRegionCode)
  const email = useAppSelector(state => state.auth.account.email)
  const canRedeemLuxPlusBenefits = useAppSelector(getCanRedeemLuxPlusBenefits)
  const canRequestLuxPlusFlightPricing = canRedeemLuxPlusBenefits && region === 'AU'

  const refresh = useCallback(async(options: Array<TransportOption>): Promise<Array<RefreshedFlight>> => {
    setStatus('refreshing')
    setRefreshedFlights([])

    const now = Date.now()
    const shouldUseCache = !hasRefreshedRef.current
    hasRefreshedRef.current = true
    const results: Array<RefreshedFlight> = []
    const paramGroups = new Map<string, { params: FlightSearchParams; options: Array<TransportOption> }>()
    const cacheKeysByItemId = new Map<number, string>()

    for (const option of options) {
      const data = option.savedItemData
      if (data?.source !== 'le_flight') continue

      const cacheKey = getRefreshCacheKey(option, currency, region, email)
      const cached = shouldUseCache ? getCachedRefreshResult(option, cacheKey, now) : undefined
      if (cached) {
        results.push(cached)
        continue
      }

      const parsed = parseSearchParams(data.searchParams)
      if (!parsed) continue

      if (cacheKey && option.itemId !== undefined) {
        cacheKeysByItemId.set(option.itemId, cacheKey)
      }

      const key = JSON.stringify(parsed)
      const existing = paramGroups.get(key)
      if (existing) {
        existing.options.push(option)
      } else {
        paramGroups.set(key, { params: parsed, options: [option] })
      }
    }

    try {
      for (const [, group] of paramGroups) {
        let searchResult: MappedFlightSearchResult
        try {
          searchResult = await searchFlights({
            itinerary: group.params.itinerary,
            currency,
            occupancies: group.params.occupancies,
            fareClass: group.params.fareClass,
            region,
            fareType: group.params.fareType,
            email,
          }, {
            enableSabreContent: true,
            ...(canRequestLuxPlusFlightPricing ? { requestLuxPlusPricing: true } : {}),
          })
        } catch {
          for (const option of group.options) {
            results.push({ option, isSoldOut: false })
          }
          continue
        }

        const newExpiry = Date.now() + FLIGHTS_SESSION_EXPIRATION_TIME_IN_MINUTES * 60 * 1000

        for (const option of group.options) {
          const data = option.savedItemData
          if (!isLeFlightSavedItemData(data)) continue

          const returnLegData = isLeFlightSavedItemData(option.returnLeg?.savedItemData)
            ? option.returnLeg.savedItemData
            : undefined
          const isReturn = Boolean(option.returnLeg && returnLegData)
          const {
            departingJourney: matchedDepartingJourney,
            returningJourney: matchedReturningJourney,
          } = findShortlistedFlightJourneys({
            onewayFares: searchResult.onewayFares,
            departingJourneyKey: data.journeyKey,
            returningJourneyKey: returnLegData?.journeyKey,
          })

          if (matchedDepartingJourney && (!isReturn || matchedReturningJourney)) {
            const departingCurrency = matchedDepartingJourney.currencyCode
            const returningCurrency = matchedReturningJourney?.currencyCode

            // Discard refresh when leg currencies disagree or fares are missing.
            const currenciesAgree =
              !isReturn
                ? Boolean(departingCurrency)
                : Boolean(departingCurrency)
                  && Boolean(returningCurrency)
                  && departingCurrency === returningCurrency

            const departingFare = matchedDepartingJourney.price.adult.totalFare
            const returningFare = matchedReturningJourney?.price.adult.totalFare
            const departingFareValid = typeof departingFare === 'number'
            const returningFareValid = !isReturn || typeof returningFare === 'number'

            if (!currenciesAgree || !departingFareValid || !returningFareValid) {
              reportLog('[TripChat] Shortlist refresh discarded', {
                reason: 'currency_or_fare_invalid',
                isReturn,
                departingCurrency,
                returningCurrency,
                departingFareValid,
                returningFareValid,
                journeyKey: data.journeyKey,
                returningJourneyKey: returnLegData?.journeyKey,
              }, 'warn')
              const result = {
                option,
                matchedJourney: matchedDepartingJourney,
                matchedDepartingJourney,
                matchedReturningJourney,
                newExpiry,
                isSoldOut: false,
              }
              cacheRefreshResult(result, cacheKeysByItemId.get(option.itemId), now)
              results.push(result)
              continue
            }

            const newPrice = isReturn ? departingFare + (returningFare ?? 0) : departingFare
            const departingRegularFare = matchedDepartingJourney.regularPrice?.adult?.totalFare
            const returningRegularFare = matchedReturningJourney?.regularPrice?.adult?.totalFare
            const isLuxPlusFare = matchedDepartingJourney.bookingInfo.isLuxPlusFare === true &&
              (!isReturn || matchedReturningJourney?.bookingInfo.isLuxPlusFare === true)
            const hasRegularFare = typeof departingRegularFare === 'number' &&
              (!isReturn || typeof returningRegularFare === 'number')
            const newRegularPrice = isLuxPlusFare && hasRegularFare ?
              isReturn ? departingRegularFare + (returningRegularFare ?? 0) : departingRegularFare :
              undefined
            const newCurrencyCode = departingCurrency

            const result = {
              option,
              matchedJourney: matchedDepartingJourney,
              matchedDepartingJourney,
              matchedReturningJourney,
              newPrice,
              newRegularPrice,
              isLuxPlusFare,
              newCurrencyCode,
              newExpiry,
              isSoldOut: false,
            }
            cacheRefreshResult(result, cacheKeysByItemId.get(option.itemId), now)
            results.push(result)
          } else {
            results.push({ option, isSoldOut: true })
          }
        }
      }

      setRefreshedFlights(results)
      setStatus('success')
      return results
    } catch {
      setStatus('error')
      return []
    }
  }, [currency, region, email, canRequestLuxPlusFlightPricing])

  return { status, refreshedFlights, refresh }
}

export default useRefreshShortlistedFlights
